import { patch } from "@src/appV2/api";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { z } from "zod";

import {
  getWorkerExternalIntegrationConfigurationUrl,
  type WorkerExternalIntegrationConfiguration,
} from "./useGetWorkerExternalIntegrationConfiguration";

const updateWorkerExternalIntegrationConfigurationRequestSchema = z.object({
  data: z.object({
    type: z.literal("worker-external-integration-configuration"),
    attributes: z.object({
      externalIntegrationType: z.string(),
      workerId: z.string(),
      tenantId: z.string(),
      externalId: z.string(),
    }),
  }),
});

const updateWorkerExternalIntegrationConfigurationResponseSchema = z.object({
  data: z.object({
    type: z.literal("worker-external-integration-configuration"),
    id: z.string(),
    attributes: z.object({
      externalIntegrationType: z.string(),
      workerId: z.string(),
      tenantId: z.string(),
      externalId: z.string(),
    }),
  }),
});

export interface UpdateWorkerExternalIntegrationConfigurationParams {
  workerId: string;
  tenantId: string;
  externalIntegrationType: string;
  externalId: string;
}

export function useUpdateWorkerExternalIntegrationConfiguration(): UseMutationResult<
  WorkerExternalIntegrationConfiguration,
  Error,
  UpdateWorkerExternalIntegrationConfigurationParams
> {
  return useMutation({
    mutationFn: async (params: UpdateWorkerExternalIntegrationConfigurationParams) => {
      const { workerId, tenantId, externalIntegrationType, externalId } = params;

      const response = await patch({
        url: getWorkerExternalIntegrationConfigurationUrl(),
        data: {
          data: {
            type: "worker-external-integration-configuration" as const,
            attributes: {
              externalIntegrationType,
              workerId,
              tenantId,
              externalId,
            },
          },
        },
        requestSchema: updateWorkerExternalIntegrationConfigurationRequestSchema,
        responseSchema: updateWorkerExternalIntegrationConfigurationResponseSchema,
      });

      return response.data.data;
    },
  });
}
